import { useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  AllEntities,
  convertEntityTypeToObjectEntityType,
  getObjectEntityTypeUILabel,
} from "../../../../components/entities/utils";
import Button from "../../../../components/ui/button/Button";
import { toast } from "../../../../components/ui/toast/Toast";
import { getUserFacingMessage } from "../../../graphqlErrors";
import { EventData } from "../../../tracking";
import { LeadEntityType } from "../../../types/graphQLEnums";
import { capitalize, pluralise } from "../../../utils";
import { OrganisationDataType } from "./leadEntityTracking";
import { useAddLeadEntities } from "./useAddLeadEntities";
import { useRemoveLeadEntities } from "./useRemoveLeadEntities";

const GONE = 410;

export type EntityActionParams = {
  entityType: AllEntities;
  entityId: string;
};

/** Buyers and suppliers both go up as `organisation`, so the event needs the side from here. */
function organisationTrackingData(entities: EntityActionParams[]): EventData {
  const sides = new Set<OrganisationDataType>();
  for (const { entityType } of entities) {
    if (entityType === "buyer") sides.add("BUYER");
    if (entityType === "supplier") sides.add("SUPPLIER");
  }
  // Both sides in one call can't be told apart per entity, so they report as ORGANISATION.
  return sides.size === 1 ? { "Data type": [...sides][0] } : {};
}

/**
 * React hook that returns wrapper functions for adding and removing a single entity from a lead.
 * Should all be the same entity type though
 * Both functions display toast notifications for success and error states.
 * Can be used to save/unsave to one or mutliple entities to/from a lead
 *
 * @param leadId - The ID of the lead to which the entity will be added or from which it will be removed.
 * @param trackingData - Optional tracking data to be used for analytics. Each add/remove call can
 * also pass its own, for attributes of that call alone.
 * @param onRemoveEntityAction - Optional React node to be displayed as an action in the toast notification when an entity is removed.
 * @param onAddEntityAction - Optional React node to be displayed as an action in the toast notification when an entity is added.
 * @returns An object containing `addLeadEntities` and `removeLeadEntities` functions.
 */
export function useLeadEntityActions({
  leadId,
  trackingData,
  onRemoveEntityAction,
  onAddEntityAction,
  onAddSuccess,
}: {
  leadId: string;
  trackingData?: EventData;
  onRemoveEntityAction?: React.ReactNode;
  onAddEntityAction?: React.ReactNode;
  onAddSuccess?: () => void;
}): {
  addLeadEntities: (params: EntityActionParams[], callTrackingData?: EventData) => void;
  removeLeadEntities: (params: EntityActionParams[], callTrackingData?: EventData) => void;
  isLoading: boolean;
} {
  const queryClient = useQueryClient();

  // Each call's own tracking data, keyed by the variables object it started its mutation with
  // (React Query hands that same object back), so overlapping calls each report their own.
  const callTracking = useRef(new WeakMap<object, EventData>());
  const trackingFor = (variables: object) => ({
    ...trackingData,
    ...callTracking.current.get(variables),
  });

  // Assuming that only one entity type is being added at a time -
  const { mutate: addEntities, isLoading: isAdding } = useAddLeadEntities(trackingFor, {
    onSuccess: (data, variables) => {
      const addedEntity = data.addLeadEntities[0];
      if (addedEntity) {
        const messageText =
          data.addLeadEntities.length > 1
            ? pluralise(
                data.addLeadEntities.length,
                getObjectEntityTypeUILabel(addedEntity.entityType),
                undefined,
              )
            : capitalize(getObjectEntityTypeUILabel(addedEntity.entityType));

        toast.success(`${messageText} saved to ${addedEntity.leadName}`, {
          action: <>{onAddEntityAction}</>,
        });
      } else {
        // The mutation returns only the rows it inserted, so an empty result means everything
        // requested was already on the lead
        const requested = variables.entities.entities[0];
        const label = requested ? getObjectEntityTypeUILabel(requested.entityType) : "entity";
        toast.info(`This ${label} is already saved to this lead`);
      }
      onAddSuccess?.();
    },
    onError: (error, variables) => {
      // 410: the data provider no longer knows the contact. The server has already dismissed any
      // suggestion for it, so refetch the lead to drop it from the list.
      const goneMessage = getUserFacingMessage(error, [GONE]);
      if (goneMessage) {
        toast.error(goneMessage);
        void queryClient.invalidateQueries(["lead", leadId]);
        return;
      }
      const entity = variables.entities.entities[0];
      toast.error(`Failed to add ${getObjectEntityTypeUILabel(entity.entityType)} to this lead`);
    },
  });

  const addLeadEntities = (addedEntities: EntityActionParams[], callTrackingData?: EventData) => {
    const variables = {
      id: leadId,
      entities: {
        // A Cognism contact has no client-side entityId — it's redeemed on save and the
        // server derives the entityId. So `entityId` here carries its stable contact id, from
        // which the server resolves a current redeem handle (redeemIds rotate).
        entities: addedEntities.map(({ entityId, entityType }) =>
          entityType === "contact_cognism"
            ? { entityType: LeadEntityType.ContactCognism, cognismContactId: entityId }
            : {
                entityId,
                entityType: convertEntityTypeToObjectEntityType(entityType, LeadEntityType),
              },
        ),
      },
    };
    callTracking.current.set(variables, {
      ...organisationTrackingData(addedEntities),
      ...callTrackingData,
    });
    addEntities(variables);
  };

  // --- REMOVE HOOK ----------------------------------------------------------
  const { mutate: removeEntities, isLoading: isRemoving } = useRemoveLeadEntities(trackingFor, {
    onSuccess: (data, variables) => {
      // Assuming only one entity type is being removed at a time
      const removedEntity = data.removeLeadEntities[0];
      // Undo re-adds what this call removed, so it reports the same side.
      const removedTracking = callTracking.current.get(variables) ?? {};

      if (!removedEntity) {
        // Should not happen, but having it just in case
        toast.error(`Error removing entity from this lead`);
        return;
      }

      const messageText =
        data.removeLeadEntities.length > 1
          ? pluralise(
              data.removeLeadEntities.length,
              getObjectEntityTypeUILabel(removedEntity.entityType),
              undefined,
            )
          : capitalize(getObjectEntityTypeUILabel(removedEntity.entityType));

      toast.success(`${messageText} removed from ${removedEntity.leadName}`, {
        action: (
          <>
            {onRemoveEntityAction ?? (
              <Button
                buttonType="tertiary"
                onClick={() => {
                  const undoVariables = {
                    id: leadId,
                    entities: {
                      entities: data.removeLeadEntities.map(({ entityId, entityType }) => ({
                        entityId,
                        // Annoying, but they are the same type. Typescript acting a fool here
                        entityType: entityType as unknown as LeadEntityType,
                      })),
                    },
                  };
                  callTracking.current.set(undoVariables, removedTracking);
                  addEntities(undoVariables);
                }}
              >
                Undo
              </Button>
            )}
          </>
        ),
      });
    },
    onError: (_, variables) => {
      const removedEntity = variables.entities.entities[0];
      toast.error(
        `Failed to remove ${getObjectEntityTypeUILabel(removedEntity.entityType)} from this lead`,
      );
    },
  });

  const removeLeadEntities = (
    removedEntities: EntityActionParams[],
    callTrackingData?: EventData,
  ) => {
    const variables = {
      id: leadId,
      entities: {
        entities: removedEntities.map(({ entityId, entityType }) => ({
          entityId,
          entityType: convertEntityTypeToObjectEntityType(entityType, LeadEntityType),
        })),
      },
    };
    callTracking.current.set(variables, {
      ...organisationTrackingData(removedEntities),
      ...callTrackingData,
    });
    removeEntities(variables);
  };

  return {
    addLeadEntities,
    removeLeadEntities,
    isLoading: isAdding || isRemoving,
  };
}
